import React, { useState, useEffect, useCallback } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";

export default function ESP32CustomDownloadCode({
  isDarkMode,
  deviceSerial = "12345",
  selectedDevice,
  onClose = () => {},
}) {
  const [ssid, setSsid] = useState("YOUR_WIFI_SSID");
  const [password, setPassword] = useState("YOUR_WIFI_PASSWORD");
  const [pinMap, setPinMap] = useState({});
  const [controlsConfig, setControlsConfig] = useState([]);
  const [sketch, setSketch] = useState("// Generando código...");
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = "https://holmio.net/app/api.php";
  const bgColorClass = isDarkMode ? "bg-gray-800" : "bg-white";
  const textColorClass = isDarkMode ? "text-gray-100" : "text-gray-800";
  const PUB_INTERVAL = 8000;

  // ✅ Normalización para C++ (reemplazar espacios por "_")
  const normalizeForCode = (desc) => desc.trim().replace(/\s+/g, "_");

  // ✅ Para MQTT (mantener original)
  const keepOriginalMQTT = (desc) => desc.trim();

  useEffect(() => {
    if (selectedDevice?.id) {
      fetchControlConfigs(selectedDevice.id);
    }
  }, [selectedDevice]);

  const fetchControlConfigs = async (deviceId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/custom_device_controls?device_id=${deviceId}`
      );
      const data = await response.json();
      if (data.status === "success") {
        setControlsConfig(data.data);
        const initialMap = {};
        data.data.forEach((c) => {
          const cfg =
            typeof c.config === "string" ? JSON.parse(c.config) : c.config;
          if (cfg?.pines && Array.isArray(cfg.pines)) {
            initialMap[c.id] = {};
            cfg.pines.forEach((p) => {
              initialMap[c.id][p] = "";
            });
          } else {
            initialMap[c.id] = { pin: c.pin_number || "" };
          }
        });
        setPinMap(initialMap);
      }
    } catch (err) {
      console.error("Error cargando configuración:", err);
    } finally {
      setLoading(false);
    }
  };

  const setField = (id, key, val) =>
    setPinMap((prev) => ({
      ...prev,
      [id]: { ...prev[id], [key]: val },
    }));

  const generateSketch = useCallback(() => {
    if (!controlsConfig.length) {
      setSketch("// No hay controles.");
      return;
    }

    let libsSet = new Set();
    let pinDefs = "";
    let initBlocks = "";
    let setupExtra = "";
    let loopBlocks = "";
    let mqttHandlers = "";
    let pwmChannelCounter = 0;

    controlsConfig.forEach((ctrl) => {
      const cfgPins = pinMap[ctrl.id];
      if (!cfgPins) return;

      const conf =
        typeof ctrl.config === "string" ? JSON.parse(ctrl.config) : ctrl.config;

      const codeDesc = normalizeForCode(ctrl.description || ctrl.control_name);
      const mqttDesc = keepOriginalMQTT(ctrl.description || ctrl.control_name);

      let localSetupInit = conf.setupInit || "";
      let localSetupExtra = conf.setupExtra || "";
      let localLoopCode = conf.loopCode || "";
      let localHandler = conf.commandHandler || "";

      // ✅ PWM Channel para sliders
      let channelId = null;
      if (conf.protocolo === "ANALOG") {
        channelId = pwmChannelCounter++;
      }

      // ✅ Pines dinámicos
      if (conf.pines && Array.isArray(conf.pines)) {
        conf.pines.forEach((p) => {
          const userPin = cfgPins[p] || 0;
          let pinMacro;

          if (conf.pines.length === 1 && p === "pin") {
            pinMacro = `PIN_${codeDesc}`;
          } else {
            pinMacro = `PIN_${codeDesc}_${p}`;
          }

          pinDefs += `#define ${pinMacro} ${userPin}\n`;

          // Reemplazos en bloques
          localSetupInit = localSetupInit.replace(new RegExp(`%PIN_${p.toUpperCase()}%`, "g"), pinMacro);
          localSetupExtra = localSetupExtra.replace(new RegExp(`%PIN_${p.toUpperCase()}%`, "g"), pinMacro);
          localLoopCode = localLoopCode.replace(new RegExp(`%PIN_${p.toUpperCase()}%`, "g"), pinMacro);
          localHandler = localHandler.replace(new RegExp(`%PIN_${p.toUpperCase()}%`, "g"), pinMacro);
        });
      } else {
        const userPin = cfgPins.pin || 0;
        const pinMacro = `PIN_${codeDesc}`;
        pinDefs += `#define ${pinMacro} ${userPin}\n`;

        localSetupInit = localSetupInit.replace(/%PIN%/g, pinMacro);
        localSetupExtra = localSetupExtra.replace(/%PIN%/g, pinMacro);
        localLoopCode = localLoopCode.replace(/%PIN%/g, pinMacro);
        localHandler = localHandler.replace(/%PIN%/g, pinMacro);
      }

      // ✅ Sustituir %DESCRIPTION_CODE% (C++)
      [localSetupInit, localSetupExtra, localLoopCode, localHandler] = [
        localSetupInit,
        localSetupExtra,
        localLoopCode,
        localHandler,
      ].map((block) =>
        block
          .replace(/%DESCRIPTION_CODE%/g, codeDesc)
          .replace(/%DESCRIPTION%/g, mqttDesc) // MQTT último
      );

      // ✅ PWM CHANNEL
      if (channelId !== null) {
        localSetupExtra = localSetupExtra.replace(/%CHANNEL%/g, channelId);
        localHandler = localHandler.replace(/%CHANNEL%/g, channelId);
      }

      if (conf.librerias) conf.librerias.forEach((lib) => libsSet.add(lib));
      if (localSetupInit) initBlocks += localSetupInit + "\n";
      setupExtra += "  " + localSetupExtra + "\n";

      if (localLoopCode) {
        loopBlocks += `
  static unsigned long last_${codeDesc}=0;
  if (millis()-last_${codeDesc}>PUB_INTERVAL){
    last_${codeDesc}=millis();
    ${localLoopCode}
  }`;
      }

      if (localHandler) {
        mqttHandlers += `
  ${localHandler}`;
      }
    });

    const ino = `/* Auto-generated by Holmio */
#include <WiFi.h>
#include <HTTPClient.h>
#include <WiFiClientSecure.h>
#include <PubSubClient.h>
#include <ArduinoJson.h>
#include "mbedtls/base64.h"
#include <time.h>
${Array.from(libsSet).join("\n")}

const unsigned long PUB_INTERVAL=${PUB_INTERVAL};

const char* WIFI_SSID="${ssid}";
const char* WIFI_PASS="${password}";
const char* CRED_URL="https://holmio.net/app/api_mqtt.php";

String serial="${deviceSerial}";
String topicBase="home/"+serial;
String clientId;

${pinDefs}${initBlocks}

WiFiClientSecure net;
PubSubClient mqtt(net);
String mqttUser,mqttPass,mqttHost;
uint16_t mqttPort=8883;

void connectWiFi();
bool downloadCredentials();
void connectMQTT();
void initTime();
void mqttCallback(char* topic, byte* payload, unsigned int length);

void setup(){
  Serial.begin(115200);
  clientId="ESP32_"+serial+"_"+String(random(1000,9999));
  connectWiFi();
  initTime();
  if(!downloadCredentials()){delay(5000); esp_restart();}
  mqtt.setCallback(mqttCallback);
  connectMQTT();
${setupExtra}}

void loop(){
  if(!mqtt.connected()) connectMQTT();
  mqtt.loop();
${loopBlocks}}

void connectWiFi(){
  WiFi.begin(WIFI_SSID,WIFI_PASS);
  while(WiFi.status()!=WL_CONNECTED) delay(300);
}

bool downloadCredentials(){
  HTTPClient http; http.begin(CRED_URL);
  if(http.GET()!=200) return false;
  StaticJsonDocument<1024> d;
  if(deserializeJson(d,http.getString())) return false;
  mqttHost=d["host"].as<String>(); mqttPort=d["port"];
  mqttUser=d["user"].as<String>(); mqttPass=d["pass"].as<String>();
  String caB64=d["caCertBase64"];
  std::vector<uint8_t> cert(2048); size_t len;
  mbedtls_base64_decode(cert.data(),cert.size(),&len,(const unsigned char*)caB64.c_str(),caB64.length());
  cert[len]='0';
  net.setCACert((const char*)cert.data());
  mqtt.setServer(mqttHost.c_str(),mqttPort);
  return true;
}

void connectMQTT(){
  mqtt.connect(clientId.c_str(),mqttUser.c_str(),mqttPass.c_str());
  if(mqtt.connected()) mqtt.subscribe((topicBase+"/+/control").c_str());
}

void mqttCallback(char* topic, byte* payload, unsigned int length){
${mqttHandlers}
}

void initTime(){
  configTime(0, 0, "pool.ntp.org", "time.nist.gov");
  time_t now = time(nullptr);
  while (now < 100000) { 
    delay(100); 
    now = time(nullptr); 
  }
  Serial.println("Tiempo sincronizado.");
}
`;
    setSketch(ino);
  }, [ssid, password, deviceSerial, pinMap, controlsConfig]);

  useEffect(generateSketch, [generateSketch]);

  const downloadSketch = () => {
    const blob = new Blob([sketch], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CustomESP32_${deviceSerial}.ino`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="p-6 text-center">Cargando configuración...</div>;
  }

  return (
    <div className={`p-6 rounded-lg shadow-xl ${bgColorClass} ${textColorClass}`}>
      <h2 className="text-lg font-semibold mb-4">Código ESP32 – {deviceSerial}</h2>
      <div className="grid sm:grid-cols-4 gap-4 mb-4">
        <label className="text-sm flex flex-col gap-1">
          <span>SSID</span>
          <input className="border rounded px-3 py-2" value={ssid} onChange={(e) => setSsid(e.target.value)} />
        </label>
        <label className="text-sm flex flex-col gap-1">
          <span>Contraseña Wi-Fi</span>
          <input type="password" className="border rounded px-3 py-2" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
      </div>

      <table className="w-full text-sm border bg-white dark:bg-gray-900 mb-4">
        <thead>
          <tr>
            <th className="border p-2">Control</th>
            <th className="border p-2">Descripción (MQTT)</th>
            <th className="border p-2">Pines GPIO</th>
            <th className="border p-2">Protocolo</th>
          </tr>
        </thead>
        <tbody>
          {controlsConfig.map((ctrl) => {
            const cfg = pinMap[ctrl.id] || {};
            const conf =
              typeof ctrl.config === "string" ? JSON.parse(ctrl.config) : ctrl.config;
            return (
              <tr key={ctrl.id}>
                <td className="border p-2">{ctrl.control_name}</td>
                <td className="border p-2">{ctrl.description}</td>
                <td className="border p-2">
                  {conf?.pines && Array.isArray(conf.pines)
                    ? conf.pines.map((p) => (
                        <input
                          key={p}
                          type="number"
                          className="border rounded w-16 px-2 py-1 m-1"
                          placeholder={p}
                          value={cfg[p] || ""}
                          onChange={(e) => setField(ctrl.id, p, e.target.value)}
                        />
                      ))
                    : (
                      <input
                        type="number"
                        className="border rounded w-20 px-2 py-1"
                        value={cfg.pin || ""}
                        onChange={(e) => setField(ctrl.id, "pin", e.target.value)}
                      />
                    )}
                </td>
                <td className="border p-2">{conf?.protocolo || "N/A"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex gap-3">
        <button onClick={generateSketch} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Actualizar código
        </button>
        <button onClick={downloadSketch} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
          Descargar .ino
        </button>
      </div>

      <div className="mt-4">
        <h3 className="font-medium mb-2 text-sm">Preview</h3>
        <div className="max-h-96 overflow-auto rounded border border-gray-700">
          <SyntaxHighlighter language="cpp" style={atomDark} customStyle={{ fontSize: "0.8rem", padding: "1rem" }} showLineNumbers>
            {sketch}
          </SyntaxHighlighter>
        </div>
      </div>
    </div>
  );
}
